import { useMutation } from '@tanstack/react-query';
import { bookServiceApi, type ApiResponse } from '@/services/api';

export interface CompleteSectionAnswerItem {
  question_id: string;
  selected_option_id: number;
  time_spent?: number;
}

export interface CompleteSectionPayload {
  section_id: number;
  reading_duration: number; // seconds
  answers: CompleteSectionAnswerItem[];
}

export interface CompleteSectionResponse {
  content_type: 'novel' | 'non_fiction' | string;
  book_id: number;
  book_title: string;
  book_category: string;

  // Streak info
  streak: boolean;
  streak_days: number;
  previous_streak: number;
  
  // Level progression
  previous_level: number;
  previous_level_xp: number;
  previous_next_level_xp: number;
  level: number;
  current_level_xp: number;
  next_level_xp: number;
  level_up: boolean;
  
  // Stats
  points: number;
  success_percentage: number;
  time: string; // mm:ss
  today_minutes: number;
  previous_minutes: number;
  
  // Goals
  daily_goal: number;
  streak_goal: number;
  daily_goal_gems?: number | null;
  daily_goal_xp?: number | null;
  streak_goal_gems?: number | null;
  streak_goal_xp?: number | null;
  
  // Level up rewards
  received_gems?: number | null;
  received_xp?: number | null;
  
  message: string;
}

export const useCompleteSection = () => {
  return useMutation<CompleteSectionResponse, Error, CompleteSectionPayload>({
    mutationFn: async (payload: CompleteSectionPayload) => {
      const res: ApiResponse<CompleteSectionResponse> = await bookServiceApi.post<CompleteSectionResponse>(
        '/completed-sections/',
        payload
      );
      if (res.success && res.data) return res.data;
      throw new Error(res.error || 'Failed to complete section');
    },
  });
};
